// components/ui/ScoreRows.tsx
import React from 'react';

const round1 = (value: number) => Math.round(value * 10) / 10;

// HOW A SCORE BREAKDOWN READS (owner, 2026-09-30: full-width bars were 'too horizontal'). One row
// per criterion, read top to bottom: its name, why, where that comes from. The points stand in a
// narrow right column with a 64px meter under them, so the eye runs DOWN one column to compare.
// Rows sit in one round card. Shared by the Fater score (Data) and the fit score (Source).
export const ScoreRow = ({ label, earned, maximum, reason, source, missingLabel, display }: {
  label: string;
  /** Null when the criterion is not evidenced. */
  earned: number | null;
  maximum: number | null;
  reason?: string | null;
  source?: string | null;
  missingLabel: string;
  /** Shown instead of earned/maximum, e.g. a percentage; the meter still draws earned/maximum. */
  display?: string;
}) => {
  const known = earned !== null && maximum !== null && maximum > 0;
  return (
    <li className="flex items-start gap-6 py-4">
      <div className="min-w-0 flex-1">
        <p className="text-[#ECECEC]" style={{ fontSize: 15, lineHeight: '22px' }}>{label}</p>
        {reason ? <p className="mt-1 text-[#8E8E8E]" style={{ fontSize: 13, lineHeight: '19px' }}>{reason}</p> : null}
        {source ? <p className="mt-1 truncate text-[#5E5E5E]" style={{ fontSize: 12, lineHeight: '16px' }} title={source}>{source}</p> : null}
      </div>
      <div className="flex w-[72px] shrink-0 flex-col items-end">
        <p className="tabular-nums text-[#8E8E8E]" style={{ fontSize: 15, lineHeight: '22px' }}>
          {known ? display ? <span className="font-medium text-[#ECECEC]" style={{ fontSize: 'inherit' }}>{display}</span> : <><span className="font-medium text-[#ECECEC]" style={{ fontSize: 'inherit' }}>{round1(earned)}</span>/{maximum}</> : '-'}
        </p>
        <div className="mt-2 h-1 w-16 overflow-hidden rounded-full bg-[#2F2F2F]" aria-hidden>
          {known ? <div className="h-full rounded-full bg-[#ECECEC]" style={{ width: `${Math.max(0, Math.min(100, (earned / maximum) * 100))}%` }} /> : null}
        </div>
        {!known ? <p className="mt-1.5 whitespace-nowrap text-[#6E6E6E]" style={{ fontSize: 12, lineHeight: '16px' }}>{missingLabel}</p> : null}
      </div>
    </li>
  );
};

/** A theme of criteria: its name and subtotal on one line (plus an optional control), its rows in one card. */
export const ScoreGroup = ({ title, earned, maximum, action, children }: {
  title: string;
  earned?: number | null;
  maximum?: number | null;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <section>
    <div className="mb-2 flex min-h-8 items-center justify-between gap-3">
      <h4 className="font-medium text-[#AFAFAF]" style={{ fontSize: 13, lineHeight: '18px' }}>{title}</h4>
      <div className="flex items-center gap-3">
        {typeof earned === 'number' && typeof maximum === 'number' ? <span className="tabular-nums text-[#8E8E8E]" style={{ fontSize: 13, lineHeight: '18px' }}>{round1(earned)}/{maximum}</span> : null}
        {action}
      </div>
    </div>
    <ul className="divide-y divide-white/[0.06] rounded-[16px] bg-[#1B1B1B] px-4">{children}</ul>
  </section>
);
